import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type AddChatMembersDto,
  type ChatAttachmentDto,
  type ChatMessageDto,
  type ChatNameDto,
  type ChatMessagesPageDto,
  type ChatPersonDto,
  type ChatReactionKind,
  type ChatSearchDto,
  type ChatThreadDto,
  type ChatUnreadDto,
  type CreateChatThreadDto,
  type EditChatMessageDto,
  type PostChatMessageDto,
  type RenameChatThreadDto,
  type SetChatNameDto,
} from '@hrms/shared';
import { del, get, patch, post, postForm, toApiErrorMessage } from '@/lib/api-client';

/**
 * The chat's reads and writes.
 *
 * Polling only, on purpose. Ten people talking at once is well within what a
 * three-second poll of an indexed range answers, and a stream would need a
 * gateway under `/api/` and a second transport to keep alive on a phone that
 * loses signal in every stairwell. If measurement ever says a poll is not
 * enough, the seq-based shape here is what a stream would nudge anyway.
 */

/** How often an open conversation asks for anything newer. */
export const MESSAGE_POLL_MS = 3_000;
/** How often the list of conversations refreshes — unread counts, new threads. */
export const THREAD_POLL_MS = 10_000;
/** How often the sidebar badge asks. */
export const UNREAD_POLL_MS = 30_000;

export const chatKeys = {
  all: ['chat'] as const,
  unread: ['chat', 'unread'] as const,
  people: ['chat', 'people'] as const,
  threads: ['chat', 'threads'] as const,
  deleted: ['chat', 'threads', 'deleted'] as const,
  names: ['chat', 'names'] as const,
  search: (q: string, threadId?: string) => ['chat', 'search', q, threadId ?? ''] as const,
  messages: (threadId: string) => ['chat', 'messages', threadId] as const,
};

/**
 * Whether chat is on for this person, and how many threads want them.
 *
 * One poll serves the sidebar badge and the switch: the server says in the
 * body whether the company has chat on and this account is still somebody
 * who works here, and the nav item goes with it. A second settings fetch
 * would need its own permission and could disagree. Until the first answer
 * arrives, and whenever the poll fails outright, the link stays — a phone in
 * a stairwell should not watch its sidebar flicker.
 */
export function useChatStatus(): {
  enabled: boolean;
  unread: number;
  latest: string | null;
  latestThreadId: string | null;
  /** False until the server has answered once; the fields above are defaults until then. */
  ready: boolean;
} {
  const { data } = useQuery({
    queryKey: chatKeys.unread,
    queryFn: () => get<ChatUnreadDto>('/chat/unread'),
    refetchInterval: UNREAD_POLL_MS,
    // Keeps asking with the page hidden: in the Android shell that is how a
    // message arriving while the phone is in a pocket reaches the tray. A
    // hidden browser tab pays one small request every thirty seconds for it.
    refetchIntervalInBackground: true,
    staleTime: 15_000,
    retry: false,
    throwOnError: false,
  });

  return {
    enabled: data?.enabled ?? true,
    unread: data?.threads ?? 0,
    latest: data?.latest ?? null,
    latestThreadId: data?.latestThreadId ?? null,
    ready: data !== undefined,
  };
}

export function usePeople(enabled = true) {
  return useQuery({
    queryKey: chatKeys.people,
    queryFn: () => get<ChatPersonDto[]>('/chat/people'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useThreads() {
  return useQuery({
    queryKey: chatKeys.threads,
    queryFn: () => get<ChatThreadDto[]>('/chat/threads'),
    refetchInterval: THREAD_POLL_MS,
    staleTime: 5_000,
  });
}

/** Conversations an administrator has deleted. Fetched only where the button is drawn. */
export function useDeletedThreads(enabled: boolean) {
  return useQuery({
    queryKey: chatKeys.deleted,
    queryFn: () => get<ChatThreadDto[]>('/chat/threads/deleted'),
    enabled,
    staleTime: 10_000,
  });
}

/**
 * Lines matching some words. Asked only once the query is long enough for the
 * server to accept it, and kept for a minute so backing out of a result and
 * in again does not search twice.
 */
export function useSearch(q: string, threadId?: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: chatKeys.search(trimmed, threadId),
    queryFn: () => get<ChatSearchDto>('/chat/search', { params: { q: trimmed, threadId } }),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  });
}

export function fetchMessages(
  threadId: string,
  params: { after?: number; before?: number; changedSince?: string } = {},
) {
  return get<ChatMessagesPageDto>(`/chat/threads/${threadId}/messages`, { params });
}

/** Refreshes everything the sidebar and the list show, after a write. */
function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: chatKeys.threads });
    void queryClient.invalidateQueries({ queryKey: chatKeys.unread });
  };
}

export function useDeleteThread() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (threadId: string) => del<void>(`/chat/threads/${threadId}`),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useRestoreThread() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (threadId: string) => post<ChatThreadDto>(`/chat/threads/${threadId}/restore`, {}),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useCreateThread() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (dto: CreateChatThreadDto) => post<ChatThreadDto>('/chat/threads', dto),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useRenameThread(threadId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (dto: RenameChatThreadDto) =>
      patch<ChatThreadDto>(`/chat/threads/${threadId}`, dto),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useAddMembers(threadId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (dto: AddChatMembersDto) =>
      post<ChatThreadDto>(`/chat/threads/${threadId}/members`, dto),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useLeaveThread(threadId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: () => del<void>(`/chat/threads/${threadId}/members/me`),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useRemoveMember(threadId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (userId: string) =>
      del<ChatThreadDto>(`/chat/threads/${threadId}/members/${userId}`),
    onSuccess: refresh,
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

/**
 * Puts one file up ahead of the message that will carry it.
 *
 * Its own request so a send that times out retries with the same attachment
 * ids and the bytes go up once. No toast: the composer shows each file's
 * state beside the draft, which is where the eye is.
 */
export function useUploadAttachment(threadId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return postForm<ChatAttachmentDto>(`/chat/threads/${threadId}/uploads`, form);
    },
  });
}

/** An edited picture in place of one on your own line. */
export function useReplaceAttachment() {
  return useMutation({
    mutationFn: ({
      messageId,
      attachmentId,
      file,
    }: {
      messageId: string;
      attachmentId: string;
      file: File;
    }) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return patch<ChatMessageDto>(`/chat/messages/${messageId}/attachments/${attachmentId}`, form, {
        headers: { 'Content-Type': undefined },
      });
    },
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useChatNames(enabled = true) {
  return useQuery({
    queryKey: chatKeys.names,
    queryFn: () => get<ChatNameDto[]>('/chat/names'),
    enabled,
    staleTime: 30_000,
  });
}

export function useSetChatName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...dto }: SetChatNameDto & { userId: string }) =>
      patch<ChatNameDto>(`/chat/names/${userId}`, dto),
    onSuccess: () => {
      // Names appear on every list and every line, so everything re-reads.
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      toast.success('Saved');
    },
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

/**
 * Sends one message. No toast on failure: the bubble itself says "not sent"
 * and offers the retry, which is a better place for it than a corner of the
 * screen that has scrolled away by the time the phone notices.
 */
export function useSendMessage(threadId: string) {
  return useMutation({
    mutationFn: (dto: PostChatMessageDto) =>
      post<ChatMessageDto>(`/chat/threads/${threadId}/messages`, dto),
  });
}

export function useMarkRead(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (seq: number) =>
      post<{ lastReadSeq: number }>(`/chat/threads/${threadId}/read`, { seq }),
    onSuccess: ({ lastReadSeq }) => {
      // Patch the list in place rather than refetching: the badge should clear
      // the moment the conversation is read, not on the next ten-second tick.
      queryClient.setQueryData<ChatThreadDto[]>(chatKeys.threads, (threads) =>
        threads?.map((thread) =>
          thread.id === threadId
            ? { ...thread, lastReadSeq, unread: Math.max(0, thread.lastSeq - lastReadSeq) }
            : thread,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: chatKeys.unread });
      // The server clears this thread's bell notices on read; refresh the bell
      // now so the badge drops with the conversation rather than on its own
      // 60-second poll. The notification keys live in the bell component; the
      // shared 'notifications' prefix matches its count and list.
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useEditMessage() {
  return useMutation({
    mutationFn: ({ id, ...dto }: EditChatMessageDto & { id: string }) =>
      patch<ChatMessageDto>(`/chat/messages/${id}`, dto),
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useDeleteMessage() {
  return useMutation({
    mutationFn: (id: string) => del<ChatMessageDto>(`/chat/messages/${id}`),
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

/**
 * Your reaction on a line. The answer is the whole line with its counts, so
 * it folds in through `absorb` like an edit does; the same kind sent again
 * takes it off, which the server decides, not the phone.
 */
export function useReact() {
  return useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: ChatReactionKind }) =>
      post<ChatMessageDto>(`/chat/messages/${id}/reaction`, { kind }),
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useUnreact() {
  return useMutation({
    mutationFn: (id: string) => del<ChatMessageDto>(`/chat/messages/${id}/reaction`),
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}
